import { CSSProperties, ReactNode, useState } from 'react';
import styles from './SideDrawer.module.scss';
import { withStyles } from '../../../utils';
import CircleButton from '../../Input/CircleButton/CircleButton';
import { Dialog } from 'radix-ui';
import Title2 from '../../Text/Title/Title2';
import { DrawerContext, useDrawerStacking } from './useDrawerStacking';

const cn = withStyles(styles);

type ContentProps = {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  title: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  style?: CSSProperties;
  modal?: boolean;
  onEscapeKeyDown?(e: KeyboardEvent): void;
};

export default function SideDrawer({
  isOpen,
  onClose: onCloseProp,
  title,
  header,
  footer,
  children,
  style,
  modal = true,
  onEscapeKeyDown,
}: ContentProps) {
  const drawerStacking = useDrawerStacking({
    isOpen,
  });

  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);

  function handleScroll(el: HTMLDivElement | null) {
    if (!el) return;

    const controller = new AbortController();

    el.addEventListener(
      'scroll',
      () => {
        setAtTop(el.scrollTop <= 5);
        setAtBottom(el.scrollHeight - el.scrollTop === el.clientHeight);
      },
      {
        signal: controller.signal,
      },
    );

    setAtBottom(el.scrollHeight - el.scrollTop === el.clientHeight);

    // AUTO-WIDTH: might want this later
    // const [width, setWidth] = useState<number | 'auto'>('auto');
    // const obs = new ResizeObserver((entries) => {
    //   const entry = entries[0];

    //   if (!entry) return;

    //   // setWidth(entry.contentRect.width);
    //   setWidth(entry.target.scrollWidth);
    // });

    // obs.observe(el);

    return () => {
      controller.abort();
      // AUTO-WIDTH: might want this later
      // obs.disconnect();
    };
  }

  return (
    <DrawerContext.Provider value={drawerStacking.providerContext}>
      <Dialog.Root
        modal={modal}
        open={isOpen}
        onOpenChange={(val) => {
          drawerStacking.handleOnOpenChange(val);
          onCloseProp?.();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className={cn(drawerStacking.isFirst && 'overlay')} />

          <Dialog.Content
            aria-describedby={undefined}
            className={cn('outerContainer', 'slideEase', drawerStacking.isFirst && modal && 'blur')}
            onInteractOutside={(e) => {
              e.preventDefault();
            }}
            style={style}
            onEscapeKeyDown={onEscapeKeyDown}
          >
            <div
              className={cn('contentCard', 'stacking')}
              style={{
                '--offsetIdx': drawerStacking.offset,
                // AUTO-WIDTH: might want this later
                // width,
                // overflow: 'hidden',
              }}
            >
              <header className={cn('header', atTop && 'atTop')}>
                <Dialog.Title asChild>
                  <Title2 level="3">{title}</Title2>
                </Dialog.Title>
                {header}
                {onCloseProp && (
                  <Dialog.Close asChild>
                    <CircleButton icon="close" size="medium" colorScheme="neutral" className={cn('close')} />
                  </Dialog.Close>
                )}
              </header>

              <div
                ref={handleScroll}
                className={cn('content', 'scrollbar', footer && 'hasFooter', atBottom && 'atBottom')}
                // AUTO-WIDTH: might want this later
                // style={{
                //   width: 'fit-content',
                // }}
              >
                {children}
              </div>

              {footer && <footer className={cn('footer', atBottom && 'atBottom')}>{footer}</footer>}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </DrawerContext.Provider>
  );
}
